// @ts-nocheck
import { MODULE_ACL, USER_ROLES } from "./constant";
import { isAclModuleOptionsRelevant } from "./aclModuleOptionsAccess";
import { parseModuleOperations } from "./voicePlanAcl";
import { AGENT_LEADS_ACL_MODULE } from "./agentLeadsQuota";
import { getUser } from "./setting";

export const CALL_CENTRE_ACL_MODULE = MODULE_ACL.CALL_CENTRE;
export const LEADS_ACL_OPTION = "leads";

function isSuperAdminRole(userRole, isSuperAdmin) {
  return isSuperAdmin || userRole === USER_ROLES.SUPERADMIN;
}

export function getCallCentreAclEntry(moduleOperations) {
  const ops = parseModuleOperations(moduleOperations);
  return ops[CALL_CENTRE_ACL_MODULE] || null;
}

export function normalizeCallCentreLeadsOptions(options) {
  if (!Array.isArray(options)) return [];
  return [
    ...new Set(
      options
        .map((o) => String(o).trim())
        .filter((o) => o === LEADS_ACL_OPTION)
    ),
  ];
}

export function hasCallCentreLeadsAcl(
  moduleOperations,
  { userRole = null, isSuperAdmin = false } = {}
) {
  if (isSuperAdminRole(userRole, isSuperAdmin)) {
    return true;
  }

  const entry = getCallCentreAclEntry(moduleOperations);
  if (!entry?.edit && !entry?.add) {
    return false;
  }

  // Legacy ACLs (before leads options): no edit_options field → all options allowed.
  if (!Array.isArray(entry.edit_options)) {
    return true;
  }

  return normalizeCallCentreLeadsOptions(entry.edit_options).includes(LEADS_ACL_OPTION);
}

function hasStandaloneAgentLeadsModule(moduleOperations, { userRole = null, isSuperAdmin = false } = {}) {
  if (isSuperAdminRole(userRole, isSuperAdmin)) {
    return true;
  }
  const ops = parseModuleOperations(moduleOperations);
  const entry = ops[AGENT_LEADS_ACL_MODULE];
  return !!(entry && (entry.view_permission || entry.add || entry.read || entry.edit));
}

/** Leads screens follow the Agent Leads module only. */
export function canUseLeads(moduleOperations, context = {}) {
  return hasStandaloneAgentLeadsModule(moduleOperations, context);
}

export function getParentGrantableCallCentreLeads(moduleOperations, isSuperAdmin) {
  if (isSuperAdmin) {
    return { canGrantLeads: true };
  }
  const entry = getCallCentreAclEntry(moduleOperations);
  if (!entry?.add && !entry?.edit) {
    return { canGrantLeads: false };
  }
  if (!Array.isArray(entry?.edit_options)) {
    return { canGrantLeads: true };
  }
  return {
    canGrantLeads: normalizeCallCentreLeadsOptions(entry.edit_options).includes(LEADS_ACL_OPTION),
  };
}

export function normalizeCallCentreLeadsAclForSave(
  { edit_options = [] } = {},
  parentGrantable = null
) {
  let options = normalizeCallCentreLeadsOptions(edit_options);
  if (parentGrantable && !parentGrantable.canGrantLeads) {
    options = [];
  }
  return { edit_options: options };
}

export function formatCallCentreLeadsAclLabel(moduleEntry) {
  if (!isAclModuleOptionsRelevant(moduleEntry)) return "—";
  const options = normalizeCallCentreLeadsOptions(moduleEntry.edit_options);
  if (!options.includes(LEADS_ACL_OPTION)) return "—";
  return "Leads";
}

export function getUserModuleOperations(user) {
  if (!user) return {};
  const raw = user?.acl_settings?.module_operations ?? user?.module_operations ?? null;
  return parseModuleOperations(raw);
}

function currentUserContext(user = getUser()) {
  return {
    ops: getUserModuleOperations(user),
    context: {
      userRole: user?.role || null,
      isSuperAdmin: user?.role === USER_ROLES.SUPERADMIN,
    },
  };
}

export function currentUserHasCallCentreLeadsAcl(user) {
  const { ops, context } = currentUserContext(user);
  return hasCallCentreLeadsAcl(ops, context);
}

export function currentUserCanUseLeads(user) {
  const { ops, context } = currentUserContext(user);
  return canUseLeads(ops, context);
}
